import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const galleryItems = [
  {
    brand: "HERMÈS Birkin 30",
    repairKey: "gallery_repair_0",
    beforeImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/b26aca441b0aca5f2f7c1a7f9f18ee9e.jpeg",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/daee196e006b33bf274f2b77ec22b361.jpeg",
  },
  {
    brand: "CHANEL Classic Flap",
    repairKey: "gallery_repair_1",
    beforeImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/d5c2f9dd81f2bfab9d4c0fdf373af52d.jpeg",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/cf1b5e266f6b3299ec9fd0ae01518fa4.jpeg",
  },
  {
    brand: "LOUIS VUITTON Speedy 35",
    repairKey: "gallery_repair_2",
    beforeImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/9314454188e3c6fc546ba8869b483b8a.jpeg",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/e8363de2195ee92ca09a9f7a6ef17db2.jpeg",
  },
  {
    brand: "DIOR Lady Dior",
    repairKey: "gallery_repair_3",
    beforeImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/1ed1c930d76ee8a0a7f05498ed5c9067.jpeg",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/dd09009c574d37112a76937c7bd8ab62.jpeg",
  },
  {
    brand: "GUCCI GG Marmont",
    repairKey: "gallery_repair_4",
    beforeImg: "https://storage.readdy-site.link/project_files/cfd4b76f-f1c2-4718-9c1c-e27c56fee3c2/55e64545-c3d5-47c9-824a-cc464c83faa9_c2bca801-1204-403a-a734-239bfb2dc43e.jpg?v=e8a37217fde4eff1e7fa3b9f53dc590c",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/bc89bfaf6619d7fd8ff4a670d008413c.jpeg",
  },
  {
    brand: "CÉLINE Box Bag",
    repairKey: "gallery_repair_5",
    beforeImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/2c55cc10076fd5c23723ccfaa017b017.jpeg",
    afterImg: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/aab8d464f763b6314a4de110125c36b5.jpeg",
  },
];

interface GalleryCardProps {
  item: typeof galleryItems[0];
}

function GalleryCard({ item }: GalleryCardProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.015] group cursor-default">
      <div className="relative flex w-full h-60 overflow-hidden">
        <div className="relative w-1/2 overflow-hidden">
          <img
            src={item.beforeImg}
            alt={`Before - ${item.brand}`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[#1A2332]/20" />
          <span
            className="absolute top-3 left-3 bg-gray-700/80 text-white text-[10px] tracking-[0.2em] px-2.5 py-1 rounded-full uppercase"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Before
          </span>
        </div>
        <div className="absolute left-1/2 top-0 h-full w-px bg-[#C9A84C] z-10" />
        <div className="relative w-1/2 overflow-hidden">
          <img
            src={item.afterImg}
            alt={`After - ${item.brand}`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span
            className="absolute top-3 right-3 bg-[#C9A84C]/90 text-[#1A2332] text-[10px] tracking-[0.2em] px-2.5 py-1 rounded-full uppercase font-bold"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            After
          </span>
        </div>
      </div>
      <div className="px-5 py-4">
        <h3
          className="font-bold text-[#1A2332] text-sm tracking-tight"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          {t(item.repairKey)}
        </h3>
      </div>
    </div>
  );
}

export default function RepairGallery() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  return (
    <section id="gallery" className="py-28 bg-[#F8F7F4] w-full">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span
            className="inline-block bg-[#C9A84C] text-[#1A2332] text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Before &amp; After
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-4 tracking-tight"
            style={{ fontFamily: heading }}
          >
            {t("gallery_label")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {galleryItems.map((item) => (
            <GalleryCard key={item.brand} item={item} />
          ))}
        </div>
        <div className="text-center mt-14">
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-lift inline-flex items-center gap-3 bg-[#1A2332] hover:bg-[#243246] text-white text-sm font-bold px-10 py-4 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: body }}
          >
            {t("gallery_cta")}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
